/**
 * Reactions — where a fact in one system becomes a consequence in another.
 *
 * This is the file that keeps Cinderwell from being four good systems standing
 * next to each other. Nothing here invents a mechanic; it connects the ones we
 * already have so the surface and the Hollow can reach each other.
 *
 * The single rule: consequences live here, not at the site of their cause.
 * `death.ts` should describe dying. What dying *costs the county* belongs here,
 * so adding a new consequence never means editing someone else's system.
 */
import { ensureSystems, observe } from "./events";
import { applySignal, controlOf, settleControl } from "./factions";
import type { Decal, SignalKind, WorldState } from "./types";

/**
 * How much attention the Hollow is paying to the surface, 0..1.
 *
 * Rises when the county gives it reasons — a corpse, a record depth, a storm,
 * an artifact sleeping in a house. Falls only while you stay up here and leave
 * it alone. It is the number that makes a run hard because of what you did last
 * week, rather than because a difficulty slider moved.
 */
export function restless(w: WorldState): number {
  return clamp01(w.restlessness ?? 0);
}

const GAIN: Partial<Record<SignalKind, number>> = {
  death: 0.14,
  depth_record: 0.09,
  descend: 0.025,
  storm: 0.05,
  artifact_stored: 0.06,
  extract: 0.01,
};

/** The one entry point. Runs after every system has had its tick. */
export function tickSystems(w: WorldState, dt: number): void {
  const l = ensureSystems(w);
  observe(l);

  for (const s of l.signals) {
    applySignal(l, s);
    const g = GAIN[s.kind];
    if (g) l.restlessness = clamp01(l.restlessness + g * Math.min(3, s.mag));
  }

  settleControl(l, dt);
  decay(l, dt);
  tickMemory(l, dt);
  l.signals.length = 0;
}

/**
 * The Hollow forgets slowly, and not at all while something of its own is
 * sitting on your bench. Shelved artifacts and Ember's grip hold a floor under
 * restlessness. That floor is the price of keeping a trophy.
 */
function decay(w: WorldState, dt: number): void {
  const floor = Math.min(0.55, shelvedCount(w) * 0.18 + controlOf(w, "ember") * 0.3);
  const cur = restless(w);
  if (cur <= floor) {
    w.restlessness = floor;
    return;
  }
  // Only the surface calms it. Time spent below is not time spent recovering.
  const rate = w.mode === "hollow" ? 0 : w.weather === "clear" ? 0.004 : 0.0018;
  w.restlessness = Math.max(floor, cur - rate * dt);
}

/**
 * Retention. The surface forgets; the Hollow does not.
 *
 * Every enemy hit, kill, and player hit pushes a decal into `w.memory`, and
 * before this nothing ever removed one — they were only aged. Measured on main:
 * 77 bytes per decal, re-serialised into localStorage every 8 seconds, 13 KB
 * base -> 167 KB after 2000 hits, growing forever. `Decal.life` was declared and
 * read by nothing.
 *
 * So `life` is now what weather spends. Rain washes the county: foot tracks go
 * first — that is the tracking window closing, and it is the reason to follow a
 * trail *now* — then blood, then ash. Death caches are never washed; a grave is
 * a marker. Nothing in the Hollow washes at all: there is no weather down
 * there, and `hollow.ts` reads blood decals to make lurchers track you.
 */
const MEMORY_CAP = 700;

/** Per-second life drain, before weather. */
const WASH: Record<string, number> = {
  foot: 0.0025,
  blood: 0.0011,
  ash: 0.0006,
  scorch: 0.0006,
};

function weatherWash(w: WorldState): number {
  if (w.weather === "storm") return 10;
  if (w.weather === "rain") return 4.5;
  if (w.weather === "fog") return 1.2;
  return 1;
}

export function tickMemory(w: WorldState, dt: number): void {
  // `world.ts` already advances `age`; this only spends `life`.
  const wx = weatherWash(w);
  for (let i = w.memory.length - 1; i >= 0; i--) {
    const d = w.memory[i]!;
    if (d.kind === "cache") continue;
    if (d.layer === "hollow") continue;
    const base = WASH[d.kind];
    if (base === undefined) continue;
    d.life -= base * wx * dt;
    if (d.life <= 0) w.memory.splice(i, 1);
  }
  if (w.memory.length > MEMORY_CAP) pruneMemory(w, MEMORY_CAP);
}

/**
 * Backstop so a long save can never grow without bound — a deep run can bleed
 * faster than any weather washes, and the Hollow never washes at all. Keeps the
 * strongest marks and never drops a death cache.
 *
 * Pure, because `saveWorld` needs to bound what it writes without quietly
 * editing the world the player is standing in.
 */
export function retainMemory(memory: Decal[], cap = MEMORY_CAP): Decal[] {
  if (memory.length <= cap) return memory;
  const caches = memory.filter((d) => d.kind === "cache");
  const rest = memory.filter((d) => d.kind !== "cache");
  rest.sort((a, b) => b.life - a.life || a.age - b.age);
  return caches.concat(rest.slice(0, Math.max(0, cap - caches.length)));
}

export function pruneMemory(w: WorldState, cap = MEMORY_CAP): void {
  w.memory = retainMemory(w.memory, cap);
}

/**
 * What an artifact does to the ground it sleeps above.
 *
 * `Crop.contamination` shipped in the first slice and was unreachable: the only
 * thing that raised it required it to already be above zero, so no crop could
 * turn and `strange_root` could not be farmed. An artifact on the bench is now
 * the thing that starts it.
 *
 * Returns contamination pressure per second at a tile.
 */
export function taintAt(w: WorldState, x: number, y: number): number {
  const shelved = shelvedCount(w);
  if (shelved <= 0) return 0;
  const door = w.overworld.homeDoor;
  const d = Math.hypot(x - door.x, y - door.y);
  const reach = 5 + shelved * 2.5;
  if (d > reach) return 0;
  const near = 1 - d / reach;
  return near * near * shelved * 0.0009 * (1 + restless(w));
}

function shelvedCount(w: WorldState): number {
  let n = 0;
  for (const b of w.home.bench) if (b && b.id === "artifact") n += 1;
  return n;
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
